/**
 * MFX Hub — widget catalogue and staff-profile definitions.
 *
 * The catalogue is the single list of widgets a dashboard can hold.
 * Each entry says which roles may add it (admins may add anything) and
 * what settings it accepts. Keep `type` ids in sync with WIDGET_TYPES in
 * app/services/hub.py — the API drops unknown types on save.
 *
 * Profiles: platform_users.role is the profile. Four staff values exist
 * and nothing new was added for the Hub:
 *   mfx_settlements  Ops
 *   mfx_pm           Trader (Portfolio Manager)
 *   mfx_compliance   Compliance
 *   mfx_admin        Admin
 */

import type { HubLayout, HubWidget, HubWidgetSize } from "@/lib/api";
import { PERM, hasAnyPermission, hasPermission } from "@/lib/permissions";

export type StaffRole = "mfx_settlements" | "mfx_pm" | "mfx_compliance" | "mfx_admin";

export const STAFF_ROLES: readonly StaffRole[] = [
  "mfx_settlements", "mfx_pm", "mfx_compliance", "mfx_admin",
];

export function isStaffRole(role: string | undefined | null): role is StaffRole {
  return !!role && (STAFF_ROLES as readonly string[]).includes(role);
}

export const ROLE_LABEL: Record<StaffRole, string> = {
  mfx_settlements: "Operations",
  mfx_pm: "Portfolio Manager",
  mfx_compliance: "Compliance",
  mfx_admin: "Admin",
};

// ── Widget catalogue ─────────────────────────────────────────

export interface WidgetSetting {
  key: string;
  label: string;
  kind: "days" | "count" | "toggle" | "select" | "pm" | "client" | "currency";
  min?: number;
  max?: number;
  default: number | string;
  // For kind === "select". Options may be gated on the user so a PM only
  // sees metrics their permissions can load.
  options?: Array<{ value: string; label: string; requires?: (user: any) => boolean }>;
}

export interface WidgetDef {
  type: string;
  title: string;
  description: string;
  // Roles that see this in "Add widget". Admins bypass.
  roles: readonly StaffRole[];
  // Extra gate on top of role, for widgets that read permission-gated
  // data (leads). Evaluated against the current user.
  requires?: (user: any) => boolean;
  settings: readonly WidgetSetting[];
  // Settings the widget edits itself (in customise mode) rather than
  // through the generic shell controls.
  defaultSettings?: Record<string, any>;
  defaultSize: HubWidgetSize;
  // Whether more than one instance makes sense on a dashboard.
  multiple?: boolean;
  // Not offered in "Add widget": made somewhere else (report widgets come
  // from the Reporting page's "Add to Hub").
  hidden?: boolean;
}

const KPI_ANY = (u: any) => hasAnyPermission(u, PERM.KPIS_ALL, PERM.KPIS_SCOPED);
const KPI_ALL = (u: any) => hasPermission(u, PERM.KPIS_ALL) || u?.role === "mfx_admin" || u?.isSuperAdmin === true;
const SEL = (key: string, label: string, def: string, options: WidgetSetting["options"]): WidgetSetting =>
  ({ key, label, kind: "select", default: def, options });

// Shared filter block for the desk widgets (bank payments, payments,
// spots, forwards, trades): who, which client, which currency, how big.
// The API applies these on top of permission scoping, which always wins.
const DESK_FILTERS: WidgetSetting[] = [
  { key: "pm", label: "PM", kind: "pm", default: "" },
  { key: "client", label: "Client", kind: "client", default: "" },
  { key: "currency", label: "Currency", kind: "currency", default: "" },
  { key: "minAmount", label: "Min amount", kind: "count", min: 0, max: 100_000_000, default: 0 },
];

const DAYS = (def: number): WidgetSetting => ({
  key: "days", label: "Look back", kind: "days", min: 1, max: 30, default: def,
});

export const WIDGET_CATALOGUE: readonly WidgetDef[] = [
  {
    type: "morning_briefing",
    title: "Morning briefing",
    description: "The 07:00 desk note: overnight news, today's calendar, and yesterday's price action on the majors.",
    roles: ["mfx_settlements", "mfx_pm", "mfx_compliance"],
    settings: [],
    defaultSize: { w: 3, h: 2 },
    multiple: true,
  },
  {
    type: "cutoff_countdown",
    title: "Cut-off countdown",
    description: "Live countdown to today's payment cut-off at each bank, per currency.",
    roles: ["mfx_settlements", "mfx_pm", "mfx_compliance"],
    settings: [],
    defaultSize: { w: 3, h: 1 },
    multiple: true,
  },
  {
    type: "settlement_headline",
    title: "Settlements queue: headline",
    description: "How many live tickets on each flow, how many are past the target and how many are flagged.",
    roles: ["mfx_settlements", "mfx_pm"],
    requires: (u: any) => hasPermission(u, PERM.SETTLEMENTS_QUEUE),
    settings: [],
    defaultSize: { w: 2, h: 1 },
    multiple: false,
  },
  {
    type: "settlement_queue",
    title: "Settlements queue",
    description: "What's in Execution, Release and Flagged, with a tab per flow.",
    roles: ["mfx_settlements", "mfx_pm"],
    requires: (u: any) => hasPermission(u, PERM.SETTLEMENTS_QUEUE),
    settings: [],
    defaultSize: { w: 3, h: 2 },
    multiple: true,
  },
  {
    type: "bp_review_queue",
    title: "Freemarket review queue",
    description: "Incoming Freemarket payments waiting to be reviewed and pushed to Salesforce.",
    roles: ["mfx_settlements", "mfx_compliance"],
    settings: [],
    defaultSize: { w: 3, h: 2 },
    multiple: false,
  },
  {
    type: "volume_all_time",
    title: "All-time volume in",
    description: "Total money in ever. Each currency totalled natively, then converted at the day's captured rate.",
    roles: ["mfx_settlements", "mfx_pm", "mfx_compliance"],
    settings: [],
    defaultSize: { w: 3, h: 2 },
    multiple: false,
  },
  {
    type: "bp_activity",
    title: "Bank Payments created",
    description: "Fully assigned Bank Payments today, yesterday, this week and this month. Toggle between count and volume.",
    roles: ["mfx_settlements", "mfx_pm", "mfx_compliance"],
    settings: [],
    defaultSize: { w: 3, h: 2 },
    multiple: false,
  },
  {
    type: "pi_activity",
    title: "Payments out created",
    description: "Payments with a beneficiary attached, over the same periods. Toggle between count and volume.",
    roles: ["mfx_settlements", "mfx_pm", "mfx_compliance"],
    settings: [],
    defaultSize: { w: 3, h: 2 },
    multiple: false,
  },
  {
    type: "bank_payments_recent",
    title: "Recent bank payments",
    description: "Incoming payments fully assigned in the last few days, across all clients.",
    roles: ["mfx_settlements", "mfx_pm", "mfx_compliance"],
    settings: [DAYS(3), ...DESK_FILTERS],
    defaultSize: { w: 6, h: 2 },
    multiple: true,
  },
  {
    type: "payments_today",
    title: "Payments due",
    description: "Pending payments with a beneficiary attached, due today or overdue.",
    roles: ["mfx_settlements", "mfx_pm"],
    settings: [...DESK_FILTERS],
    defaultSize: { w: 3, h: 2 },
    multiple: true,
  },
  {
    type: "spots_today",
    title: "Spots settling today",
    description: "Spot trades value-dated today.",
    roles: ["mfx_settlements", "mfx_pm"],
    settings: [...DESK_FILTERS],
    defaultSize: { w: 3, h: 2 },
    multiple: true,
  },
  {
    type: "forwards_far_this_week",
    title: "Forwards maturing this week",
    description: "Live forwards whose far date falls Monday to Sunday of the current week.",
    roles: ["mfx_settlements", "mfx_pm"],
    settings: [SEL("window", "Window", "this_week", [
      { value: "this_week", label: "This week (Mon–Sun)" },
      { value: "7", label: "Next 7 days" },
      { value: "14", label: "Next 14 days" },
      { value: "30", label: "Next 30 days" },
    ]), ...DESK_FILTERS],
    defaultSize: { w: 6, h: 2 },
    multiple: true,
  },
  {
    type: "trades_recent",
    title: "Recent trades",
    description: "Every trade type booked in the last few days.",
    roles: ["mfx_pm", "mfx_settlements"],
    settings: [DAYS(7), ...DESK_FILTERS],
    defaultSize: { w: 3, h: 2 },
    multiple: true,
  },
  {
    type: "leads_follow_ups",
    title: "Lead callbacks",
    description: "Leads with a callback due today or overdue.",
    roles: ["mfx_pm"],
    requires: (u) => hasAnyPermission(u, PERM.LEADS_ALL, PERM.LEADS_SCOPED),
    settings: [
      { key: "daysAhead", label: "Include next", kind: "days", min: 0, max: 30, default: 0 },
    ],
    defaultSize: { w: 3, h: 2 },
    multiple: true,
  },
  {
    type: "fx_pairs",
    title: "FX rates",
    description: "Live bid/ask for the pairs you choose, with today's move and a 30-day sparkline.",
    roles: ["mfx_settlements", "mfx_pm", "mfx_compliance"],
    settings: [],
    defaultSettings: { pairs: ["GBPUSD", "EURGBP", "EURUSD", "GBPEUR"] },
    defaultSize: { w: 3, h: 2 },
    multiple: true,
  },
  {
    type: "economic_calendar",
    title: "Economic calendar",
    description: "Upcoming releases this week, grouped by day. Toggle high-impact only in settings.",
    roles: ["mfx_settlements", "mfx_pm", "mfx_compliance"],
    settings: [
      { key: "highOnly", label: "High impact only", kind: "toggle", default: 0 },
      { key: "currency", label: "Currency", kind: "currency", default: "" },
    ],
    defaultSize: { w: 3, h: 2 },
    multiple: true,
  },
  {
    type: "articles",
    title: "Central bank articles",
    description: "Latest press releases and speeches from the ECB, BoE and Fed.",
    roles: ["mfx_settlements", "mfx_pm", "mfx_compliance"],
    settings: [
      SEL("source", "Source", "", [
        { value: "", label: "All" }, { value: "boe", label: "Bank of England" },
        { value: "ecb", label: "ECB" }, { value: "fed", label: "Federal Reserve" },
      ]),
      { key: "limit", label: "Show", kind: "count", min: 3, max: 30, default: 12 },
    ],
    defaultSize: { w: 3, h: 2 },
    multiple: true,
  },
  {
    type: "risk_summary",
    title: "Forward risk",
    description: "Open forward exposure, net MTM and offside total, with the clients currently offside.",
    roles: ["mfx_settlements", "mfx_pm", "mfx_compliance"],
    requires: (u) => hasPermission(u, PERM.RISK),
    settings: [
      { key: "pm", label: "PM", kind: "pm", default: "" },
      { key: "client", label: "Client", kind: "client", default: "" },
    ],
    defaultSize: { w: 3, h: 2 },
    multiple: true,
  },
  {
    type: "risk_stress",
    title: "Stress test",
    description: "Offside exposure if every open pair moves against clients by 1 / 2 / 3 / 5 / 10%.",
    roles: ["mfx_settlements", "mfx_pm", "mfx_compliance"],
    requires: (u) => hasPermission(u, PERM.RISK),
    settings: [
      { key: "pm", label: "PM", kind: "pm", default: "" },
      { key: "client", label: "Client", kind: "client", default: "" },
    ],
    defaultSize: { w: 2, h: 2 },
    multiple: true,
  },
  // ── KPI widgets ─────────────────────────────────────────────
  // All reuse the KPI endpoints, which already scope PMs to their own
  // book server-side. Admin-only metrics are gated in `options.requires`.
  {
    type: "kpi_headline",
    title: "KPI headline",
    description: "One number, big — or a goal bar. Pick the metric. Add as many as you like.",
    roles: ["mfx_pm", "mfx_settlements", "mfx_compliance"],
    requires: KPI_ANY,
    settings: [SEL("metric", "Metric", "ytdRevenue", [
      { value: "ytdRevenue", label: "Trade revenue YTD", requires: KPI_ALL },
      { value: "ytdNetProfit", label: "YTD net profit", requires: KPI_ALL },
      { value: "yoyPct", label: "YoY revenue %", requires: KPI_ALL },
      { value: "varianceToTarget", label: "Variance to target (YTD)", requires: KPI_ALL },
      { value: "runRateFullYear", label: "Full-year run rate", requires: KPI_ALL },
      { value: "activeClients", label: "Active clients" },
      { value: "active25kGoal", label: "Active clients over £25k/yr (goal 400)" },
      { value: "periodRevenue", label: "Revenue (current period)" },
      { value: "periodVolume", label: "Volume (current period)" },
      { value: "periodTrades", label: "Trades (current period)" },
      { value: "periodMargin", label: "Realised margin (current period)" },
      { value: "netProfit", label: "Net profit (this month)" },
      { value: "netMarginPct", label: "Net margin % (this month)" },
    ])],
    defaultSize: { w: 2, h: 1 },
    multiple: true,
  },
  {
    type: "kpi_profit",
    title: "Profit",
    description: "Dealt or settled trade profit: one number for the current period, or a bar or line chart going back as many periods as you like.",
    roles: ["mfx_pm", "mfx_settlements", "mfx_compliance"],
    requires: KPI_ANY,
    settings: [
      SEL("basis", "Profit", "dealt", [
        { value: "dealt", label: "Dealt (when booked)" },
        { value: "settled", label: "Settled (paid and disbursed)" },
      ]),
      SEL("period", "Per", "month", [
        { value: "day", label: "Day" },
        { value: "week", label: "Week" },
        { value: "month", label: "Month" },
        { value: "quarter", label: "Quarter" },
        { value: "year", label: "Year" },
      ]),
      SEL("display", "Show as", "number", [
        { value: "number", label: "Number (current period)" },
        { value: "bar", label: "Bar chart" },
        { value: "line", label: "Line chart" },
      ]),
      { key: "count", label: "Periods (charts, 1 = current only)", kind: "count", min: 1, max: 36, default: 6 },
      { key: "split", label: "Split by trade type", kind: "toggle", default: 0 },
    ],
    defaultSize: { w: 2, h: 1 },
    multiple: true,
  },
  {
    type: "kpi_monthly_chart",
    title: "Monthly revenue chart",
    description: "Revenue by month — against target and forecast, or this year versus last.",
    roles: ["mfx_pm", "mfx_settlements", "mfx_compliance"],
    requires: KPI_ANY,
    settings: [SEL("series", "Series", "yoy", [
      { value: "yoy", label: "This year vs last year" },
      { value: "target", label: "Revenue vs target & forecast", requires: KPI_ALL },
    ])],
    defaultSize: { w: 4, h: 2 },
    multiple: true,
  },
  {
    type: "kpi_top_clients",
    title: "Top clients",
    description: "Ranked client list by revenue, net profit, or biggest movers year on year.",
    roles: ["mfx_pm", "mfx_settlements", "mfx_compliance"],
    requires: KPI_ANY,
    settings: [
      SEL("basis", "Rank by", "cyYtdRevenue", [
        { value: "cyYtdRevenue", label: "YTD revenue" },
        { value: "runRateFy", label: "Full-year run rate" },
        { value: "gainers", label: "Top gainers YoY" },
        { value: "decliners", label: "Top decliners YoY" },
        { value: "netProfit", label: "YTD net profit", requires: KPI_ALL },
      ]),
      { key: "limit", label: "Show", kind: "count", min: 3, max: 50, default: 10 },
    ],
    defaultSize: { w: 3, h: 2 },
    multiple: true,
  },
  {
    type: "kpi_team",
    title: "Team leaderboard",
    description: "Traders or consultants ranked by revenue, YoY change, or net contribution.",
    roles: ["mfx_pm", "mfx_settlements", "mfx_compliance"],
    requires: KPI_ALL,
    settings: [
      SEL("role", "Team", "trader", [{ value: "trader", label: "Traders" }, { value: "consultant", label: "Consultants" }]),
      SEL("metric", "Rank by", "cyYtdRevenue", [
        { value: "cyYtdRevenue", label: "YTD revenue" },
        { value: "yoyChangeGbp", label: "YoY change £" },
        { value: "netContribution", label: "Net contribution" },
        { value: "cyYtdMarginPct", label: "Margin %" },
        { value: "clientCount", label: "Clients" },
      ]),
    ],
    defaultSize: { w: 3, h: 2 },
    multiple: true,
  },
  {
    type: "kpi_person",
    title: "Trader card",
    description: "One person's YTD: revenue, YoY, clients, margin, biggest client. Add one per trader.",
    roles: ["mfx_pm", "mfx_settlements", "mfx_compliance"],
    requires: KPI_ALL,
    settings: [SEL("role", "Team", "trader", [{ value: "trader", label: "Traders" }, { value: "consultant", label: "Consultants" }])],
    defaultSettings: { personId: "" },
    defaultSize: { w: 2, h: 1 },
    multiple: true,
  },
  {
    type: "kpi_client_flags",
    title: "Client watchlist",
    description: "Clients by performance flag — at risk, declining, growing — with YTD and YoY.",
    roles: ["mfx_pm", "mfx_settlements", "mfx_compliance"],
    requires: KPI_ANY,
    settings: [
      SEL("flag", "Flag", "At Risk", [
        { value: "At Risk", label: "At risk" },
        { value: "Declining", label: "Declining" },
        { value: "Growing", label: "Growing" },
        { value: "New", label: "New" },
        { value: "Dormant", label: "Dormant" },
        { value: "", label: "All" },
      ]),
      { key: "limit", label: "Show", kind: "count", min: 3, max: 50, default: 10 },
    ],
    defaultSize: { w: 3, h: 2 },
    multiple: true,
  },
  {
    type: "kpi_action_plan",
    title: "Action plan",
    description: "Accounts down quarter on quarter — who to call.",
    roles: ["mfx_pm", "mfx_settlements", "mfx_compliance"],
    requires: KPI_ANY,
    settings: [
      { key: "minDeclinePct", label: "Min decline %", kind: "count", min: 5, max: 100, default: 20 },
      { key: "limit", label: "Show", kind: "count", min: 3, max: 50, default: 10 },
    ],
    defaultSize: { w: 3, h: 2 },
    multiple: true,
  },
  {
    type: "kpi_vertical_mix",
    title: "Revenue by vertical",
    description: "YTD revenue split by client vertical.",
    roles: ["mfx_pm", "mfx_settlements", "mfx_compliance"],
    requires: KPI_ANY,
    settings: [],
    defaultSize: { w: 2, h: 2 },
    multiple: true,
  },
  {
    type: "kpi_profitability",
    title: "Profitability",
    description: "Revenue, costs and net profit for a period.",
    roles: ["mfx_pm", "mfx_settlements", "mfx_compliance"],
    requires: KPI_ANY,
    settings: [SEL("period", "Period", "current_month", [
      { value: "current_month", label: "This month" },
      { value: "previous_month", label: "Last month" },
      { value: "current_quarter", label: "This quarter" },
      { value: "previous_quarter", label: "Last quarter" },
      { value: "current_year", label: "Year to date" },
      { value: "previous_year", label: "Last year" },
    ])],
    defaultSize: { w: 3, h: 1 },
    multiple: true,
  },
  // ── Manager widgets ─────────────────────────────────────────
  {
    type: "kpi_team_pulse",
    title: "Team pulse",
    description: "Per trader: revenue this month vs the same point last month, trades and clients MTD, new clients this quarter, and days since they last onboarded someone.",
    roles: ["mfx_pm", "mfx_settlements", "mfx_compliance"],
    requires: KPI_ANY,
    settings: [SEL("sort", "Sort by", "revenueMtd", [
      { value: "revenueMtd", label: "Revenue MTD" },
      { value: "momPct", label: "Month-on-month %" },
      { value: "newClientsQtd", label: "New clients this quarter" },
      { value: "daysSinceNewClient", label: "Longest since a new client" },
      { value: "tradesMtd", label: "Trades MTD" },
    ])],
    defaultSize: { w: 4, h: 2 },
    multiple: true,
  },
  {
    type: "lead_activity_team",
    title: "Lead activity — team",
    description: "Per PM over the window: calls logged, follow-ups set, leads taken on, pushed to Salesforce, closed, live pipeline and overdue callbacks.",
    roles: ["mfx_pm", "mfx_settlements", "mfx_compliance"],
    requires: (u) => hasAnyPermission(u, PERM.LEADS_ALL, PERM.LEADS_SCOPED),
    settings: [SEL("days", "Window", "7", [
      { value: "7", label: "Last 7 days" }, { value: "14", label: "Last 14 days" }, { value: "30", label: "Last 30 days" },
    ])],
    defaultSize: { w: 4, h: 2 },
    multiple: true,
  },
  {
    type: "lead_activity_person",
    title: "Lead activity — person",
    description: "One PM's lead numbers with a calls-per-day sparkline. Add one per new starter.",
    roles: ["mfx_pm", "mfx_settlements", "mfx_compliance"],
    requires: (u) => hasAnyPermission(u, PERM.LEADS_ALL, PERM.LEADS_SCOPED),
    settings: [SEL("days", "Window", "7", [
      { value: "7", label: "Last 7 days" }, { value: "14", label: "Last 14 days" }, { value: "30", label: "Last 30 days" },
    ])],
    defaultSettings: { pmId: "" },
    defaultSize: { w: 2, h: 1 },
    multiple: true,
  },
  {
    type: "report",
    title: "Report",
    description: "An answer from the Reporting page, refreshed on a schedule. Made with \"Add to Hub\" on Reporting.",
    roles: ["mfx_settlements", "mfx_pm", "mfx_compliance"],
    requires: (u) => hasPermission(u, PERM.REPORTING),
    settings: [],
    defaultSize: { w: 3, h: 2 },
    multiple: true,
    hidden: true,
  },
  {
    type: "quick_nav",
    title: "Quick links",
    description: "Buttons that jump straight to the tools you use most.",
    roles: ["mfx_settlements", "mfx_pm", "mfx_compliance"],
    settings: [],
    defaultSize: { w: 3, h: 2 },
  },
];

export const WIDGET_BY_TYPE: Record<string, WidgetDef> = Object.fromEntries(
  WIDGET_CATALOGUE.map((w) => [w.type, w]),
);

/** Widgets the current user is allowed to add. */
export function availableWidgets(user: any): WidgetDef[] {
  const role = user?.role;
  const isAdmin = role === "mfx_admin" || user?.isSuperAdmin === true;
  return WIDGET_CATALOGUE.filter((w) => {
    if (w.hidden) return false;
    if (w.requires && !w.requires(user)) return false;
    if (isAdmin) return true;
    return isStaffRole(role) && w.roles.includes(role);
  });
}

export function newWidgetId(): string {
  return `w${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

export function makeWidget(def: WidgetDef): HubWidget {
  const settings: Record<string, any> = { ...(def.defaultSettings ?? {}) };
  for (const s of def.settings) settings[s.key] = s.default;
  return { id: newWidgetId(), type: def.type, size: def.defaultSize, settings };
}

export const EMPTY_LAYOUT: HubLayout = { widgets: [], quickNav: [] };

// ── Quick-nav targets ─────────────────────────────────────────
// What a user may pin. Filtered by the same gates the sidebar uses so a
// pinned link can never point somewhere the nav wouldn't.

export interface QuickNavTarget { label: string; to: string }

export function quickNavTargets(user: any): QuickNavTarget[] {
  const role = user?.role;
  const isSuper = user?.isSuperAdmin === true;
  const out: QuickNavTarget[] = [
    { label: "Clients", to: "/clients" },
    { label: "Charts", to: "/charts" },
    { label: "Articles", to: "/articles" },
    { label: "Economic Calendar", to: "/calendar" },
    { label: "Messages", to: "/messages" },
    { label: "Statements", to: "/statements" },
  ];
  if (hasPermission(user, PERM.COMMISSIONS)) out.push({ label: "Commissions", to: "/commissions" });
  if (hasPermission(user, PERM.INVITE_CLIENT)) out.push({ label: "Invite Client", to: "/invite" });
  if (hasAnyPermission(user, PERM.KPIS_ALL, PERM.KPIS_SCOPED)) out.push({ label: "KPIs", to: "/kpis" });
  if (hasPermission(user, PERM.OPS_TOOLS)) {
    out.push({ label: "Operations Tools", to: "/operations-tools" });
    out.push({ label: "Recon", to: "/operations-tools/recon" });
  }
  if (hasPermission(user, PERM.COMPLIANCE_TOOLS)) out.push({ label: "Compliance Tools", to: "/compliance-tools" });
  if (hasPermission(user, PERM.RISK)) out.push({ label: "Risk Management", to: "/risk" });
  if (role === "mfx_pm" || role === "mfx_settlements" || role === "mfx_admin" || isSuper) out.push({ label: "Trades", to: "/trade-booking" });
  if (hasAnyPermission(user, PERM.LEADS_ALL, PERM.LEADS_SCOPED)) out.push({ label: "Leads", to: "/leads" });
  if (hasPermission(user, PERM.CONTROL_CENTRE)) out.push({ label: "Control Centre", to: "/control-centre" });
  if (hasPermission(user, PERM.NOTIFICATIONS)) out.push({ label: "Notifications", to: "/admin/notifications" });
  if (role === "mfx_admin" || role === "mfx_compliance" || role === "mfx_pm" || isSuper) {
    out.push({ label: "Client Documents", to: "/client-documents" });
  }
  if (role === "mfx_admin") {
    out.push({ label: "Admin", to: "/admin" });
    out.push({ label: "Cut-off times", to: "/settings/cutoffs" });
  }
  if (isSuper) {
    out.push({ label: "Super Admin", to: "/super-admin" });
    out.push({ label: "Client-Facing Pages", to: "/client-facing" });
  }
  return out;
}
